import { assign } from 'lodash';

import { ColumnSettings } from '@mode/legacy/data-access-model-js';
import ColumnOptionsService from './column-options-service';
import DatasetService from './dataset-service';

class TableDataService {
  public static $inject = ['DatasetService', 'ColumnOptionsService'];

  public static DISPLAY_ROW_LIMIT = 1000;
  public static MIN_FILTER_QUERY_LENGTH = 2;

  public constructor(
    private ds: DatasetService,
    private cos: ColumnOptionsService
  ) {}

  public hasRows(queryToken) {
    const data = this.ds.getDatasetByKey(queryToken);
    return data && data.count > 0;
  }

  public dataOversized(queryToken) {
    const data = this.ds.getDatasetByKey(queryToken);

    if (data && typeof data.oversized === 'boolean') {
      return data.oversized;
    } else {
      return null;
    }
  }

  public rowCount(queryToken) {
    if (this.hasRows(queryToken)) {
      return this.ds.getDatasetByKey(queryToken).content.length;
    } else {
      return null;
    }
  }

  public columnCount(queryToken) {
    return this.ds.getDatasetByKey(queryToken).columns.length;
  }

  public dataset(queryToken: string, tableToken: string, opts = {}, dataset = null) {
    if (!queryToken && !dataset) {
      throw new Error('Dataset query token or dataset undefined');
    }
    if (!tableToken) {
      throw new Error('Table viz token undefined');
    }
    if (!this.ds.hasContentFor(queryToken)) {
      throw new Error('Dataset could not be found');
    }
    const colTypeFn = this.resolveColumnType.bind(this, queryToken, tableToken);
    const options = assign(
      {},
      {
        value: (data, colKey) => () => this.ds.getValue.call(null, data, colTypeFn(colKey)),
        sorter: this.ds.sortTable.bind(this, colTypeFn),
        rowLimit: TableDataService.DISPLAY_ROW_LIMIT,
      },
      opts
    );

    return this.ds.getDatasetModel(queryToken, options, dataset);
  }

  public resolveColumnType(queryToken: string, settings: ColumnSettings, colKey: string) {
    const DEFAULT_VALUE_TYPE = 'plain-text';
    const availableTypes = ['plain-text', 'date', 'financial', 'currency', 'number', 'percentage'];
    const selectedColumnType = this.cos.columnType(settings, colKey) || 'automatic';
    const resolveSelectedType = (type) => {
      if (!!~availableTypes.indexOf(type)) {
        return type;
      } else {
        return DEFAULT_VALUE_TYPE;
      }
    };

    if (!selectedColumnType || selectedColumnType === 'automatic') {
      return this.ds.resolveColumnDataType(queryToken, colKey);
    } else {
      return resolveSelectedType(selectedColumnType);
    }
  }

  public datasetState(queryToken: string) {
    return this.ds.getDatasetState(queryToken);
  }

  public queryData(queryToken: string) {
    return this.ds.getDatasetByKey(queryToken);
  }
}

export default TableDataService;
